/**
 * 이메일 도메인 안내 — 가입·이메일 변경 입력 아래에 붙는 인라인 배너.
 *
 *  · 오타 도메인    → 빨간 배너 + '바꾸기' 버튼 (제출해도 서버가 400 으로 막는다)
 *  · 카카오 계열    → 노란 배너 (차단하지 않음. 휴면 잠금 위험만 알린다)
 *
 * 근거: 2026-08-10 발송함 반송 전수 분석. hanmail/kakao 반송 16통 중 15통이
 * 550 5.2.1 "Mailbox is inactive" — 주소 오류가 아니라 카카오의 휴면 계정 잠금이었다.
 */
import { FiAlertCircle, FiInfo, FiArrowRight } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { inspectEmailDomain } from '../../lib/emailDomain'

type Props = {
  email: string
  /** '바꾸기' 클릭 시 정정된 주소를 돌려준다. 없으면 버튼을 감춘다. */
  onApplySuggestion?: (fixed: string) => void
  className?: string
}

export default function EmailDomainNotice({ email, onApplySuggestion, className = '' }: Props) {
  const { t } = useTranslation()
  const hint = inspectEmailDomain(email)
  if (!hint) return null

  if (hint.suggestion && hint.suggestedEmail) {
    return (
      <div
        role="alert"
        className={`rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5 ${className}`}
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-red-600 dark:text-red-300">
          <FiAlertCircle size={15} className="shrink-0" aria-hidden="true" />
          <span className="line-through decoration-red-500/60">{hint.domain}</span>
          <FiArrowRight size={13} className="shrink-0 opacity-70" aria-hidden="true" />
          <span>{hint.suggestion}</span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-muted">
          {t('auth.emailDomain.typoDesc')}
        </p>
        {onApplySuggestion && (
          <button
            type="button"
            onClick={() => onApplySuggestion(hint.suggestedEmail as string)}
            className="mt-2 rounded-md border border-red-500/50 bg-red-500/10 px-2.5 py-1 text-xs font-medium
                       text-red-700 transition-colors hover:bg-red-500/20 dark:text-red-200"
          >
            {t('auth.emailDomain.applyFix', { email: hint.suggestedEmail })}
          </button>
        )}
      </div>
    )
  }

  if (hint.dormantRisk) {
    return (
      <div className={`rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 ${className}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
          <FiInfo size={15} className="shrink-0" aria-hidden="true" />
          <span>{t('auth.emailDomain.dormantTitle', { domain: hint.domain })}</span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-muted">
          {t('auth.emailDomain.dormantDesc')}
        </p>
      </div>
    )
  }

  return null
}
